// src/components/CustomFunnel.tsx
import React, { useCallback, useMemo, useState } from "react";
import { useCreateCustomFunnelLeadMutation } from "../../app/api/customFunnelApi";

type FormState = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNo: string;
  funnelName: string;
  source?: string;
};

const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const CustomFunnel: React.FC = () => {
  const [form, setForm] = useState<FormState>({
    firstName: "",
    lastName: "",
    email: "",
    phoneNo: "",
    funnelName: "Business Sales Funnel — Whitelabel",
    source: "website_banner",
  });

  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [createLead, { isLoading }] = useCreateCustomFunnelLeadMutation();

  const onChange = useCallback((k: keyof FormState, v: string) => {
    setForm((s) => ({ ...s, [k]: v }));
    setErrorMessage(null);
    setStatusMessage(null);
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      setErrorMessage(null);
      setStatusMessage(null);

      if (!form.firstName || !form.email) {
        setErrorMessage("Please enter your name and a valid email address.");
        return;
      }

      if (!isValidEmail(form.email)) {
        setErrorMessage("Please enter a valid email address.");
        return;
      }

      try {
        const payload = {
          firstName: form.firstName.trim(),
          lastName: form.lastName?.trim(),
          email: form.email.trim().toLowerCase(),
          phoneNo: form.phoneNo?.trim(),
          funnelName: form.funnelName,
          source: form.source,
          meta: {
            submittedAt: new Date().toISOString(),
          },
        };

        // call RTK Query mutation and unwrap result to catch errors
        await createLead(payload).unwrap();

        setStatusMessage(
          "🎉 You're pre-registered! We'll email you next steps and an onboarding call invite."
        );

        // reset form (keep funnelName & source)
        setForm((s) => ({
          firstName: "",
          lastName: "",
          email: "",
          phoneNo: "",
          funnelName: s.funnelName,
          source: s.source,
        }));
      } catch (err: any) {
        console.error("Create custom funnel lead error:", err);
        const msg =
          err?.data?.message ||
          err?.message ||
          "Failed to submit. Please try again or contact support.";
        setErrorMessage(msg);
      }
    },
    [createLead, form]
  );

  const ctaText = useMemo(() => {
    if (isLoading) return "Submitting...";
    if (statusMessage) return "Pre-registered ✔";
    return "Pre-Register My Business";
  }, [isLoading, statusMessage]);

  return (
    <section className="bg-white py-16">
      <div className="max-w-5xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: marketing copy (benefits-focused) */}
          <div>
            <div className="inline-block bg-yellow-400 text-brandNavy px-4 py-1 rounded-full font-semibold mb-4">
              Business Sales Funnel — Whitelabel
            </div>

            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-4">
              Get customers your own way — done-for-you funnels that scale
            </h2>

            <p className="text-lg text-gray-700 mb-6">
              We build, deploy, and manage high-converting sales funnels for
              businesses that want thousands of customers without the headache.
              Stop chasing tools and start getting predictable sales, reliable
              customer growth, and automated follow-up that actually converts.
            </p>

            <ul className="space-y-3 text-gray-700 mb-6">
              <li className="flex items-start gap-3">
                <span className="text-2xl">✅</span>
                <div>
                  <strong className="block">Predictable lead flow</strong>
                  Proven funnels that consistently capture & nurture your ideal
                  customers.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-2xl">⚙️</span>
                <div>
                  <strong className="block">Fully managed</strong>
                  We build it from scratch — landing pages, email sequences,
                  payment integration, and analytics.
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-2xl">💸</span>
                <div>
                  <strong className="block">Sales-first focus</strong>
                  Funnels optimized to convert so you start seeing revenue
                  within weeks.
                </div>
              </li>
            </ul>

            <div className="text-sm text-gray-600">
              Ideal for: small-to-medium businesses, agencies, product launches,
              and entrepreneurs who want a plug-and-play sales engine.
            </div>
          </div>

          {/* Right: form */}
          <div>
            <div className="bg-gray-50 p-8 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold mb-4">
                Pre-Register Your Business
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Join the priority list for the{" "}
                <strong>Whitelabel Business Sales Funnel</strong>.
                Pre-registered businesses get early onboarding, priority launch
                scheduling, and exclusive onboarding discounts.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="First name"
                    value={form.firstName}
                    onChange={(e) => onChange("firstName", e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:outline-none focus:border-yellow-400"
                    required
                  />
                  <input
                    type="text"
                    placeholder="Last name (optional)"
                    value={form.lastName}
                    onChange={(e) => onChange("lastName", e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:outline-none focus:border-yellow-400"
                  />
                </div>

                <input
                  type="email"
                  placeholder="Business email"
                  value={form.email}
                  onChange={(e) => onChange("email", e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:outline-none focus:border-yellow-400"
                  required
                />

                <input
                  type="tel"
                  placeholder="Phone number (WhatsApp preferred)"
                  value={form.phoneNo}
                  onChange={(e) => onChange("phoneNo", e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 focus:outline-none focus:border-yellow-400"
                />

                {/* hidden funnel name (keeps it editable if needed) */}
                <input
                  type="text"
                  value={form.funnelName}
                  onChange={(e) => onChange("funnelName", e.target.value)}
                  className="hidden"
                />

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-yellow-400 text-gray-900 font-bold py-3 rounded-lg hover:brightness-95 transition disabled:opacity-60"
                >
                  {ctaText}
                </button>

                {errorMessage && (
                  <div className="text-sm text-red-600 mt-2">
                    {errorMessage}
                  </div>
                )}
                {statusMessage && (
                  <div className="text-sm text-green-700 mt-2">
                    {statusMessage}
                  </div>
                )}

                <p className="text-xs text-gray-500 mt-3">
                  By pre-registering you agree to be contacted about onboarding
                  and priority offers. We respect your privacy — email only
                  unless you opt-in for SMS.
                </p>
              </form>
            </div>

            {/* small trust / credibility */}
            <div className="mt-4 text-xs text-gray-600">
              <div>
                Built by The Blueprint Architect — plug-and-play funnels for
                measurable growth.
              </div>
              <div className="mt-2">
                Secure payments & data — GDPR-friendly collection.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CustomFunnel;
